import { useState, useMemo, useCallback } from 'react';

export interface UseTableConfig<T> {
  initialSortBy?: keyof T;
  initialSortDirection?: 'asc' | 'desc';
  pageSize: number;
  initialFilters?: Partial<Record<keyof T, string>>;
  paginationType?: 'traditional' | 'showMore';
}

export interface UseTableReturn<T> {
  // Data
  data: T[];
  filteredData: T[];
  
  // Pagination
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  
  // Show More functionality
  itemsToShow: number;
  hasMoreItems: boolean;
  showMore: () => void;
  showAll: () => void;
  resetToInitial: () => void;
  
  // Sorting
  sortBy: keyof T | null;
  sortDirection: 'asc' | 'desc';
  setSorting: (column: keyof T) => void;
  
  // Filtering
  filters: Partial<Record<keyof T, string>>;
  setFilter: (column: keyof T, value: string) => void;
  clearFilters: () => void;
  
  // Search
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

export const useTable = <T extends Record<string, any>>(
  initialData: T[],
  config: UseTableConfig<T>
): UseTableReturn<T> => {
  const {
    initialSortBy = null,
    initialSortDirection = 'asc',
    pageSize: initialPageSize,
    initialFilters = {},
    paginationType = 'showMore',
  } = config;

  // State
  const [sortBy, setSortBy] = useState<keyof T | null>(initialSortBy);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [itemsToShow, setItemsToShow] = useState(initialPageSize);
  const [filters, setFilters] = useState<Partial<Record<keyof T, string>>>(initialFilters);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = initialData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply column filters
    Object.entries(filters).forEach(([column, filterValue]) => {
      if (filterValue) {
        filtered = filtered.filter((item) =>
          String(item[column as keyof T])
            .toLowerCase()
            .includes(filterValue.toLowerCase())
        );
      }
    });

    return filtered;
  }, [initialData, searchTerm, filters]);

  // Sorted data
  const sortedData = useMemo(() => {
    if (!sortBy) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      // Compare values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortBy, sortDirection]);

  // Displayed data (Show More pattern or Traditional pagination)
  const displayedData = useMemo(() => {
    if (paginationType === 'showMore') {
      return sortedData.slice(0, itemsToShow);
    } else {
      // Traditional pagination
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      return sortedData.slice(startIndex, endIndex);
    }
  }, [sortedData, itemsToShow, currentPage, pageSize, paginationType]);

  // Total pages and items
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const totalItems = filteredData.length;
  const hasMoreItems = itemsToShow < sortedData.length;

  // Show More handlers
  const showMore = useCallback(() => {
    setItemsToShow(prev => Math.min(prev + pageSize, sortedData.length));
  }, [pageSize, sortedData.length]);

  const showAll = useCallback(() => {
    setItemsToShow(sortedData.length);
  }, [sortedData.length]);

  const resetToInitial = useCallback(() => {
    setItemsToShow(initialPageSize);
  }, [initialPageSize]);

  // Sorting handler
  const setSorting = useCallback((column: keyof T) => {
    setSortBy(prevSortBy => {
      setSortDirection(prevDirection => {
        if (prevSortBy === column) {
          // Toggle direction if same column
          return prevDirection === 'asc' ? 'desc' : 'asc';
        } else {
          // Set new column with ascending direction
          return 'asc';
        }
      });
      return column;
    });
    // Reset pagination when sorting changes
    if (paginationType === 'showMore') {
      setItemsToShow(initialPageSize);
    } else {
      setCurrentPage(1);
    }
  }, [paginationType, initialPageSize]);

  // Filter handler
  const setFilter = useCallback((column: keyof T, value: string) => {
    setFilters(prev => ({
      ...prev,
      [column]: value,
    }));
    // Reset pagination when filters change
    if (paginationType === 'showMore') {
      setItemsToShow(initialPageSize);
    } else {
      setCurrentPage(1);
    }
  }, [paginationType, initialPageSize]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
    if (paginationType === 'showMore') {
      setItemsToShow(initialPageSize);
    } else {
      setCurrentPage(1);
    }
  }, [paginationType, initialPageSize]);

  // Page size handler
  const handleSetPageSize = useCallback((size: number) => {
    setPageSize(size);
    if (paginationType === 'showMore') {
      setItemsToShow(size);
    } else {
      setCurrentPage(1);
    }
  }, [paginationType]);

  return {
    data: displayedData,
    filteredData,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    setCurrentPage,
    setPageSize: handleSetPageSize,
    itemsToShow,
    hasMoreItems,
    showMore,
    showAll,
    resetToInitial,
    sortBy,
    sortDirection,
    setSorting,
    filters,
    setFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
  };
}; 